import React, { useState } from "react";
import { Link } from "react-router-dom";
import { NOINDEX_PRIVATE_SEO } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import { forgotPassword, resetPassword } from "../../services/authApi";
import "./auth-pages.css";

export default function ForgotPasswordPage() {
  usePageSeo({
    ...NOINDEX_PRIVATE_SEO,
    title: "Reset password | Treydora",
    description: "Reset your Treydora account password.",
  });

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const onRequestCode = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setStatusMessage("");
    setBusy(true);
    try {
      const res = await forgotPassword(email.trim());
      setStatusMessage(
        res?.message || "If an account exists for this email, we sent password reset instructions."
      );
      setStep(2);
    } catch (err) {
      setErrorMessage(err?.message || "Unable to send reset code right now.");
    } finally {
      setBusy(false);
    }
  };

  const onReset = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setStatusMessage("");
    if (newPassword !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await resetPassword({ email: email.trim(), code: code.trim() }, newPassword);
      setStatusMessage("Password updated. You can sign in with your new password.");
      setStep(3);
    } catch (err) {
      setErrorMessage(err?.message || "Unable to reset password. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="td-auth-page">
      <div className="td-auth-page__card">
        <p className="td-auth-page__brand">Treydora</p>
        <h1 className="td-auth-page__title">Reset password</h1>
        <p className="td-auth-page__lead">
          We never reveal whether an address exists. Check your inbox for a six-digit code, then choose
          a new password.
        </p>

        {step === 1 && (
          <form className="td-auth-page__form" onSubmit={onRequestCode}>
            <input
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              required
            />
            <button type="submit" className="oshar-btn-gold" style={{ width: "100%" }} disabled={busy}>
              {busy ? "Sending…" : "Send reset code"}
            </button>
          </form>
        )}

        {step === 2 && (
          <form className="td-auth-page__form" onSubmit={onReset}>
            <input type="email" autoComplete="email" placeholder="Email" value={email} onChange={(ev) => setEmail(ev.target.value)} required />
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              placeholder="6-digit code"
              value={code}
              onChange={(ev) => setCode(ev.target.value.replace(/\D/g, "").slice(0, 6))}
              required
            />
            <input
              type="password"
              autoComplete="new-password"
              placeholder="New password"
              value={newPassword}
              onChange={(ev) => setNewPassword(ev.target.value)}
              required
              minLength={6}
            />
            <input
              type="password"
              autoComplete="new-password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(ev) => setConfirmPassword(ev.target.value)}
              required
              minLength={6}
            />
            <button type="submit" className="oshar-btn-gold" style={{ width: "100%" }} disabled={busy}>
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}

        {step === 3 && (
          <p className="td-auth-page__success">
            {statusMessage || "Password updated."}{" "}
            <Link to="/login">Back to sign in</Link>
          </p>
        )}

        {errorMessage ? <p className="td-auth-page__err">{errorMessage}</p> : null}
        {step === 2 && statusMessage ? <p className="td-auth-page__success">{statusMessage}</p> : null}

        <div className="td-auth-page__links">
          <Link to="/login">Back to sign in</Link>
          {step > 1 && step < 3 ? (
            <button
              type="button"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "#e6cf8b",
                font: "inherit",
                textDecoration: "underline",
              }}
              onClick={() => {
                setStep(1);
                setCode("");
                setNewPassword("");
                setConfirmPassword("");
                setStatusMessage("");
                setErrorMessage("");
              }}
            >
              Start over
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
